import React, {useEffect, useState} from "react";
import Immutable from "immutable";
import {AutoSizer, Column, SortDirection, Table, CellMeasurer, CellMeasurerCache} from "react-virtualized";
import TableVirtualizedStyle from "./virtualizedStyle";
import "react-virtualized/styles.css";
import {useWindowSize} from "../../util/DominantUtil";
import Link from "next/link";

const titleBarHeight = 40;

export default function TableVirtualized({
                                             mutableData,
                                             columns,
                                             defaultSortBy,
                                             defaultSortDirection,
                                             setBgClassName,
                                             numberFormat,
                                             customHeight,
                                             market = "financial-market",
                                             scrollToIndex,
                                         }) {
    const immutableData = Immutable.List(mutableData);
    const [sortBy, setSortedBy] = useState(defaultSortBy ? defaultSortBy : 'index');
    const [sortDirection, setSortDirection] = useState(defaultSortDirection === 'DESC' ? SortDirection.DESC : SortDirection.ASC);
    const [selected, setSelected] = useState(null);
    const {scaledHeight, width} = useWindowSize()
    const inMobileMode = width && width < 700
    const minHeight = scaledHeight < 600 ? 600 : scaledHeight ;
    const height = customHeight ? customHeight : minHeight - titleBarHeight;

    const _sortList = ({sortBy, sortDirection}) => {
        if (mutableData && mutableData.length > 0) {
            return immutableData
                .sortBy((item) => item[sortBy])
                .update((list) =>
                    sortDirection === SortDirection.DESC ? list.reverse() : list
                );
        }
    };

    const [sortedList, setSortList] = useState(Immutable.List([]));
    
    useEffect(() => {
        setSortList(_sortList({sortBy, sortDirection}));
    }, [mutableData]);

    function _getDatum(list, index) {
        if (list._tail) setClassName(list._tail.array[index]);
        return list.get(index % list.size);
    }

    const setClassName = (row) => {
        return row;
    };

    const rowGetter = ({index}) => {
      if (sortedList === null || sortedList.size === 0) {
        return {}; // Return an empty object or any default value when sortedList is not initialized
      }
      return _getDatum(sortedList, index);
    };

    const _sort = ({sortBy, sortDirection}) => {
        setSelected(sortBy)
        const sortedList = _sortList({sortBy, sortDirection});
        setSortList(sortedList);
        setSortDirection(sortDirection);
        setSortedBy(sortBy);
    };

    function changeCellData(data, item) {
        let value = data
        if (item.renderer) value = item.renderer(value)
        if (typeof data === 'number') value = value.toLocaleString()
        if (item.type === 'link') {
            if (item.dataIndex !== 'faN') {
                value = <Link className='persian-font' href={`/${market}/${value}`}><a>{value}</a></Link>
            } else {
                value = <Link href={`/${market}/${data}`}><a>{data}</a></Link>
            }
        }
        return <div
            className={`w-100 low-shadow ${item.colorRenderer && item.colorRenderer(value)} ${defaultClassName(data, item)}
            ${getBgClassName(item, data)}`}>{item.suffix ? value + item.suffix : value}
        </div>
    }

    function getBgClassName(item, data) {
        const def = 'bg-white';
        try {
            let className = '';
            className += ' ' + (setBgClassName ? setBgClassName(data) : def);
            return className
        } catch {
            return 'bg-white'
        }
    }

    const cellCache = new CellMeasurerCache({
      fixedWidth: true,
      defaultHeight: 20, // keep this <= any actual row height
      minHeight: 10,     // keep this <= any actual row height
    });

    function getCellParent() {
        const aMeasuredColumnIndex = 2; // any measured column index will do

        let rowParent = null; // let a cellRenderer supply a usable value

        return { // an intermediary between measured row cells
                             //   and their true containing Grid
          invalidateCellSizeAfterRender: ({rowIndex}) => {
            if (rowParent &&
                  typeof rowParent.invalidateCellSizeAfterRender == 'function') {
              rowParent.invalidateCellSizeAfterRender({columnIndex: 0, rowIndex});
            }
          },
        }
    }

    function measuredCellRenderer({rowIndex, columnIndex, parent, cellData}, item) {
      const rowParent = parent; // parent is the Table's grid,
                          //   save it for use by rowRenderer
      return (
        <CellMeasurer
          cache={cellCache}
          columnIndex={columnIndex}
          parent={getCellParent()}
          rowIndex={rowIndex}
        >
          <div className="h-100">{changeCellData(cellData, item)}</div>
        </CellMeasurer>
      );
    }

    function defaultClassName(num, item) {
        if (item.className) return item.className;
        else if (typeof num === "number") return "english-font"
        return "rtl"
    }


    return (
        <div>
            <TableVirtualizedStyle/>
            <AutoSizer disableHeight>
                {({width}) => (
                    <Table
                        headerClassName={'headerColumn'}
                        headerHeight={30}
                        height={height}
                        rowHeight={45}
                        rowGetter={rowGetter}
                        rowCount={mutableData && mutableData.length}
                        sort={_sort}
                        sortBy={sortBy}
                        sortDirection={sortDirection}
                        width={width}
                        scrollToPosition={0}
                        scrollToIndex={scrollToIndex}
                    >
                        {columns.map(item => {
                            if (!item.enable || (inMobileMode && item.disableOnMobile)) return false
                            return (
                                <Column
                                    key={item.dataIndex}
                                    label={item.title}
                                    dataKey={item.dataIndex}
                                    disableSort={!item.sorter}
                                    className={item.className ? item.className : ''}
                                    width={width}
//                                    cellRenderer={({cellData}) => cellRender(cellData, item.dataIndex, item.renderer, item.suffix, item.colorRenderer)}
                                    cellRenderer={(x) => measuredCellRenderer(x, item)}
                                />
                            );
                        })}
                    </Table>
                )}
            </AutoSizer>
        </div>
    );
}
